/*
 * Classifies a failed dynamic `import()` of a Module Federation remote.
 *
 * A failed federated import always throws the same opaque
 * `TypeError: Failed to fetch dynamically imported module` regardless of the
 * real cause (CORS, 404, dead remote, parse error...). The error object carries
 * no detail, so we recover it with a two-stage probe of the remote-entry URL:
 *
 *   fetch(url, { mode: 'cors' })
 *     resolves 404      → REMOTE_NOT_FOUND
 *     resolves >= 500   → REMOTE_HTTP_ERROR
 *     resolves ok       → MODULE_PARSE_ERROR  (network + CORS fine; remote code broke)
 *     aborted (timeout) → TIMEOUT
 *     rejects → fetch(url, { mode: 'no-cors' })
 *                 resolves (opaque) → CORS_MISCONFIG     (server reachable, headers wrong)
 *                 rejects           → REMOTE_UNREACHABLE (down / DNS / refused)
 *
 * The no-cors probe is the deterministic CORS signal: it succeeds whenever the
 * server is reachable, so a cors-mode failure paired with a no-cors success can
 * only mean missing/incorrect CORS headers.
 *
 * Results are memoized per probed URL (see classificationCache) so repeated
 * failures of the same remote reuse the verdict instead of re-probing. Entries
 * expire after CLASSIFICATION_TTL_MS (1 min) so a URL whose status flips — e.g.
 * the intermittent CORS cache-poisoning (max-age=30) — is re-probed rather than
 * pinned to a stale first verdict.
 */

const PROBE_TIMEOUT_MS = 3000;
const CLASSIFICATION_TTL_MS = 60_000;

// Classification results keyed by the probed URL, as { result, storedAt }.
// OFFLINE is never cached since it reflects mutable navigator state rather than
// the remote itself.
const classificationCache = new Map();

const IMPORT_ERROR_CATEGORY = {
  OFFLINE: 'OFFLINE',
  CORS_MISCONFIG: 'CORS_MISCONFIG',
  REMOTE_NOT_FOUND: 'REMOTE_NOT_FOUND',
  REMOTE_HTTP_ERROR: 'REMOTE_HTTP_ERROR',
  REMOTE_UNREACHABLE: 'REMOTE_UNREACHABLE',
  MODULE_PARSE_ERROR: 'MODULE_PARSE_ERROR',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN',
};

const probe = async (url, mode) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return await fetch(url, { mode, cache: 'no-store', signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const runClassification = async (remoteUrl, error) => {
  // Cheap, deterministic pre-checks before spending a network probe.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return IMPORT_ERROR_CATEGORY.OFFLINE;
  }
  if (error?.name === 'SyntaxError') {
    return IMPORT_ERROR_CATEGORY.MODULE_PARSE_ERROR;
  }
  if (!remoteUrl) {
    return IMPORT_ERROR_CATEGORY.UNKNOWN;
  }

  let corsResponse;
  try {
    corsResponse = await probe(remoteUrl, 'cors');
  } catch (corsError) {
    if (corsError?.name === 'AbortError') {
      return IMPORT_ERROR_CATEGORY.TIMEOUT;
    }
    // cors-mode rejected — see whether the server is reachable at all.
    try {
      await probe(remoteUrl, 'no-cors');
      return IMPORT_ERROR_CATEGORY.CORS_MISCONFIG;
    } catch (noCorsError) {
      if (noCorsError?.name === 'AbortError') {
        return IMPORT_ERROR_CATEGORY.TIMEOUT;
      }
      return IMPORT_ERROR_CATEGORY.REMOTE_UNREACHABLE;
    }
  }

  if (corsResponse.status === 404) {
    return IMPORT_ERROR_CATEGORY.REMOTE_NOT_FOUND;
  }
  if (corsResponse.status >= 500) {
    return IMPORT_ERROR_CATEGORY.REMOTE_HTTP_ERROR;
  }
  if (corsResponse.ok) {
    // The entry fetched fine, so the import failed evaluating the module
    // (broken remote build or shared-dependency mismatch).
    return IMPORT_ERROR_CATEGORY.MODULE_PARSE_ERROR;
  }
  return IMPORT_ERROR_CATEGORY.UNKNOWN;
};

/**
 * Classify why a federated dynamic import failed. Never throws.
 * @param {string|null} remoteUrl - the remote-entry URL to probe (see remoteUrls.js)
 * @param {Error} error - the error thrown by the failed import()
 * @returns {Promise<string>} one of IMPORT_ERROR_CATEGORY.
 */
const classifyImportError = async (remoteUrl, error) => {
  if (remoteUrl) {
    const cached = classificationCache.get(remoteUrl);
    if (cached && Date.now() - cached.storedAt < CLASSIFICATION_TTL_MS) {
      return cached.result;
    }
  }

  let result;
  try {
    result = await runClassification(remoteUrl, error);
  } catch {
    result = IMPORT_ERROR_CATEGORY.UNKNOWN;
  }

  if (remoteUrl && result !== IMPORT_ERROR_CATEGORY.OFFLINE) {
    classificationCache.set(remoteUrl, { result, storedAt: Date.now() });
  }
  return result;
};

export { classifyImportError, IMPORT_ERROR_CATEGORY, PROBE_TIMEOUT_MS };
